// request-a-stay — the rules a stay request is judged by, in one place.
//
// THE SERVER JUDGES EVERY DATE (AC24). The availability endpoint and the
// request write both call these, and the wizard draws what they return; the
// phone's clock and the phone's arithmetic decide nothing. Pure functions, so
// every rule is proved by a unit test rather than by a screen that happened to
// look right (row 3's lesson: a green static render proved nothing about an
// effect).
//
// Nothing here reads the database. The endpoint counts the dogs already in the
// book and hands those counts in; these decide what the counts mean.

import { PORTAL_SETTING_DEFAULTS } from "./enums";
import { AREA_RATES, DEFAULT_AREA_RATE, perNightForDogs } from "./pricing";
import type { CapacityLine, HandoverDayRule, Period } from "./enums";
import type { LimitPair } from "./types";

// ----- Christmas ------------------------------------------------------------

/**
 * THE CHRISTMAS WINDOW AND ITS CHARGE — Patricia's settings (`christmas_from`,
 * `christmas_to`, `christmas_surcharge`), seeded at 15 Dec to 6 Jan and €5 a
 * night (Freddi, 2026-10-08). The default IS the seed, read from the one list
 * of defaults, so a missing setting can never quietly change a price.
 */
export interface ChristmasRule {
  /** `MM-DD`, both ends in. */
  from: string;
  to: string;
  /** Euro per night, on top of the rate. */
  surcharge: number;
}

const MONTH_DAY = /^(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/;

export const CHRISTMAS_DEFAULT: ChristmasRule = {
  from: PORTAL_SETTING_DEFAULTS.christmas_from,
  to: PORTAL_SETTING_DEFAULTS.christmas_to,
  surcharge: Number(PORTAL_SETTING_DEFAULTS.christmas_surcharge),
};

/** The rule from the settings rows; a key missing or unreadable keeps its seeded default. */
export function christmasRule(s: { christmas_from?: string; christmas_to?: string; christmas_surcharge?: string }): ChristmasRule {
  const md = (v: string | undefined, d: string) => (v && MONTH_DAY.test(v) ? v : d);
  const charge = Number(s.christmas_surcharge);
  return {
    from: md(s.christmas_from, CHRISTMAS_DEFAULT.from),
    to: md(s.christmas_to, CHRISTMAS_DEFAULT.to),
    surcharge: s.christmas_surcharge !== undefined && s.christmas_surcharge !== "" && Number.isFinite(charge) && charge >= 0 ? charge : CHRISTMAS_DEFAULT.surcharge,
  };
}

/**
 * Is the night of this date inside the Christmas window? Both ends count. A
 * window whose start is later in the year than its end crosses New Year.
 */
export function isChristmasNight(iso: string, rule: ChristmasRule = CHRISTMAS_DEFAULT): boolean {
  const md = iso.slice(5);
  return rule.from <= rule.to ? md >= rule.from && md <= rule.to : md >= rule.from || md <= rule.to;
}

// ----- dates ----------------------------------------------------------------

function addDay(iso: string, n: number): string {
  const d = new Date(`${iso}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

/** The nights a stay sleeps: check-in up to the night before check-out. */
export function nightsOf(checkIn: string, checkOut: string): string[] {
  const out: string[] = [];
  for (let d = checkIn; d < checkOut; d = addDay(d, 1)) out.push(d);
  return out;
}

const WEEKDAY = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const WEEKDAY_KEY = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"];

function weekday(iso: string): number {
  return new Date(`${iso}T12:00:00Z`).getUTCDay();
}

/** `2026-10-20` → `Tue 20` — how the prototype names a day in a note. */
export function shortDay(iso: string): string {
  return `${WEEKDAY[weekday(iso)]} ${Number(iso.slice(8, 10))}`;
}

/** `17:00-18:00` → `5–6pm`; `11:30-12:30` → `11:30am–12:30pm`. Anything else as it came. */
export function shortWindow(window: string): string {
  const m = /^(\d{1,2}):(\d{2})-(\d{1,2}):(\d{2})$/.exec(window.trim());
  if (!m) return window;
  const part = (h: number, min: string) => {
    const hour = h % 12 === 0 ? 12 : h % 12;
    return { text: min === "00" ? `${hour}` : `${hour}:${min}`, half: h < 12 ? "am" : "pm" };
  };
  const a = part(Number(m[1]), m[2]!);
  const b = part(Number(m[3]), m[4]!);
  return a.half === b.half ? `${a.text}–${b.text}${b.half}` : `${a.text}${a.half}–${b.text}${b.half}`;
}

// ----- price ----------------------------------------------------------------

export interface StayQuote {
  nights: number;
  /** The household's nightly rate before any Christmas charge. */
  ratePerNight: number;
  christmasNights: number;
  total: number;
  /** min(deposit minimum, total) — decision 4. */
  deposit: number;
  balance: number;
  /** No balance: the whole stay is the deposit (c7f). */
  inFull: boolean;
  /** Patricia's deposit minimum — what "under €100" is under, on `c7f`. */
  depositMinimum: number;
}

/**
 * THE PRICE THE DESK WOULD CHARGE (R-REQ-7). The household's own rate where
 * the desk has set one, otherwise the modal's per-night arithmetic
 * (`perNightForDogs`) — the same function, so a request and a desk booking for
 * the same dogs cost the same. An unsized dog is priced at the big rate, the
 * line it is counted on (AC20). Christmas nights carry the configured charge
 * more each (AC7).
 */
export function quoteStay(args: {
  checkIn: string;
  checkOut: string;
  sizes: readonly (string | null | undefined)[];
  defaultRate?: number | null;
  depositMinimum: number;
  christmas?: ChristmasRule;
}): StayQuote {
  const xmas = args.christmas ?? CHRISTMAS_DEFAULT;
  const nights = nightsOf(args.checkIn, args.checkOut);
  const priced = args.sizes.map((s) => (s === "big" || s === "little" ? s : "big"));
  const ratePerNight =
    args.defaultRate != null && args.defaultRate > 0 ? args.defaultRate : perNightForDogs(priced, AREA_RATES.big ?? DEFAULT_AREA_RATE);
  const christmasNights = nights.filter((n) => isChristmasNight(n, xmas)).length;
  const total = Math.round((nights.length * ratePerNight + christmasNights * xmas.surcharge) * 100) / 100;
  const deposit = Math.min(args.depositMinimum, total);
  const balance = Math.round((total - deposit) * 100) / 100;
  return { nights: nights.length, ratePerNight, christmasNights, total, deposit, balance, inFull: balance === 0, depositMinimum: args.depositMinimum };
}

// ----- the gate -------------------------------------------------------------

export type HandoverState = "open" | "pm_only" | "am_only" | "closed";

/**
 * When the gate is open on a day, from what Patricia keeps: the weekly rule
 * (`handover_closed_weekly`, `["sun:am"]`) and her dated rows (`handover_days`,
 * handover-days AC9). A dated row BEATS the weekday on every day it covers — a
 * Closed on a Sunday closes it, and nothing about Sundays reopens it.
 */
export function handoverFor(
  day: string,
  rules: { closedWeekly: readonly string[]; dated: readonly { startsOn: string; endsOn: string; rule: HandoverDayRule }[] },
): HandoverState {
  const dated = rules.dated.find((d) => d.startsOn <= day && day <= d.endsOn);
  if (dated) return dated.rule;
  const key = WEEKDAY_KEY[weekday(day)]!;
  const am = rules.closedWeekly.includes(`${key}:am`);
  const pm = rules.closedWeekly.includes(`${key}:pm`);
  if (am && pm) return "closed";
  if (am) return "pm_only";
  if (pm) return "am_only";
  return "open";
}

// ----- capacity -------------------------------------------------------------

export type LineCounts = Record<CapacityLine, number>;
export type LineLimits = Record<CapacityLine, LimitPair>;
export type DayLevel = "free" | "limited" | "full";

/**
 * The lines this household's dogs stand on. AN UNSIZED DOG COUNTS AS BIG — the
 * stricter line — so it can make a day read full but never free when it is not
 * (AC19, AC20). The same rule counts the dogs already in the book.
 */
export function householdLines(sizes: readonly (string | null | undefined)[]): LineCounts {
  const out: LineCounts = { kennel: 0, big: 0, little: 0 };
  for (const s of sizes) {
    out.kennel += 1;
    if (s === "little") out.little += 1;
    else out.big += 1;
  }
  return out;
}

/**
 * Free, limited or full FOR THIS HOUSEHOLD on one day — never a count (§8).
 *
 * Projected = the dogs already in + this household's. A line is full when the
 * projection goes OVER its hard limit — exactly at it still fits — and limited
 * from its soft limit ("Limited from 50 / Full at 65"). Only the lines the
 * household has a dog on are asked, plus the kennel line, which every dog
 * stands on. The worst line answers (AC18).
 */
export function dayLevel(existing: LineCounts, mine: LineCounts, limits: LineLimits): DayLevel {
  let worst: DayLevel = "free";
  for (const line of ["kennel", "big", "little"] as const) {
    if (mine[line] === 0) continue;
    const projected = existing[line] + mine[line];
    const { soft, hard } = limits[line];
    if (projected > hard) return "full";
    if (soft !== null && projected >= soft) worst = "limited";
  }
  return worst;
}

// ----- the range ------------------------------------------------------------

export interface AvailabilityDay {
  day: string;
  level: DayLevel | "past";
  handover: HandoverState;
  /** A night of a stay this household already has — marked quietly on the calendar (Freddi, 2026-10-08). */
  mine?: boolean;
}

/** A stay the household already has: its first night and its collection day. */
export interface OwnStay {
  checkIn: string;
  checkOut: string;
}

export type NoteKind = "long" | "mine" | "full" | "closed" | "limited" | "sunday" | "christmas" | "clear" | "past";
export type NoteTone = "stop" | "mute" | "ok";

export interface RangeNote {
  kind: NoteKind;
  tone: NoteTone;
  headline: string;
  body: string;
}

export interface RangeVerdict {
  /** In consequence order, at most three conditions under the confirmation; each one line. */
  notes: RangeNote[];
  /** A note that stops the booking, or no range at all. */
  blocked: boolean;
  /** Both days and both handover times chosen, and nothing blocks. */
  ready: boolean;
  /** The handover times the stay will use — a Sunday end is set to the afternoon (AC31). */
  dropOff: Period | null;
  collection: Period | null;
}

const MAX_NOTES = 3;

/**
 * ONE LINE PER NOTE (Freddi, 2026-10-08: "1 liners … never 2 liners even when
 * only one message visible or 5"). A fact and its reason — the fact bold, the
 * reason quiet beside it — short enough to sit on one line of a phone. One
 * wording, whatever the stack's height: a note that read differently alone and
 * in company was two notes pretending to be one.
 */
type Draft = { kind: NoteKind; line: [string, string] };

function tone(kind: NoteKind): NoteTone {
  return kind === "long" || kind === "mine" || kind === "full" || kind === "closed" || kind === "past" ? "stop" : kind === "clear" ? "ok" : "mute";
}

/** A period the gate refuses on this day, or null when it is open then. */
function shut(state: HandoverState, period: Period | null): boolean {
  if (state === "closed") return true;
  if (period === null) return false;
  return (state === "pm_only" && period === "am") || (state === "am_only" && period === "pm");
}

/**
 * WHAT THE DATES SAY BACK (flow 2.2). Every condition that applies is said,
 * each once, in consequence order — the ones that stop the booking first,
 * then the ones that only inform — at most three, each one line (AC4).
 *
 * A BLOCKER IN A STACK IS STILL A BLOCKER (decision 195): a full night or a
 * handover on a closed day stops the booking wherever it sits in the stack. The
 * prototype's caption on `c6ax` says "still bookable"; it is stale against 195
 * and the criteria win.
 *
 * A closed day the stay runs THROUGH says nothing — only a handover can land
 * on a closed day. The collection day is not a night, so it cannot be full.
 */
export function judgeRange(args: {
  days: readonly AvailabilityDay[];
  checkIn: string | null;
  checkOut: string | null;
  dropOff: Period | null;
  collection: Period | null;
  pmWindow: string;
  today: string;
  christmas?: ChristmasRule;
  /** The household's own stays — a range may not share a night with one. */
  ownStays?: readonly OwnStay[];
}): RangeVerdict {
  const xmas = args.christmas ?? CHRISTMAS_DEFAULT;
  const empty = { notes: [], blocked: true, ready: false, dropOff: args.dropOff, collection: args.collection };
  const { checkIn, checkOut } = args;
  if (!checkIn || !checkOut) return empty;
  if (checkOut <= checkIn) return empty;

  if (checkIn < args.today) {
    const note: RangeNote = { kind: "past", tone: "stop", headline: `${shortDay(checkIn)} has gone`, body: "pick a day from today" };
    return { ...empty, notes: [note] };
  }

  const byDay = new Map(args.days.map((d) => [d.day, d]));
  const at = (day: string): AvailabilityDay => byDay.get(day) ?? { day, level: "free", handover: "open" };
  const nights = nightsOf(checkIn, checkOut);
  const drafts: Draft[] = [];

  // ONE STAY AT A TIME (Freddi, 2026-10-08). The household's dogs cannot be in
  // twice: a range sharing a NIGHT with a stay it already has — a request
  // still waiting included — stops. Starting the day another is collected, or
  // ending the day another is dropped off, shares no night and is fine.
  const clash = (args.ownStays ?? []).find((s) => checkIn < s.checkOut && checkOut > s.checkIn);
  if (clash) {
    drafts.push({ kind: "mine", line: ["Overlaps your stay", `${shortDay(clash.checkIn)} – ${shortDay(clash.checkOut)}`] });
  }

  const fullNight = nights.find((n) => at(n).level === "full");
  if (fullNight) {
    const d = shortDay(fullNight);
    drafts.push({ kind: "full", line: [`${d} is full`, "no stay can run over it"] });
  }

  let dropOff = args.dropOff;
  let collection = args.collection;
  const ends: [string, "drop-offs" | "collections", Period | null][] = [
    [checkIn, "drop-offs", dropOff],
    [checkOut, "collections", collection],
  ];
  const sundays: string[] = [];
  for (const [day, what, period] of ends) {
    const state = at(day).handover;
    if (state === "pm_only") {
      // AC31: the morning is off on this end and the afternoon is set.
      if (what === "drop-offs") dropOff = "pm";
      else collection = "pm";
      sundays.push(day);
      continue;
    }
    if (shut(state, period)) {
      const d = shortDay(day);
      // NEVER "CLOSED" (Freddi, 2026-10-08): the kennel is open and dogs stay
      // through it — only the handover is off, and only an END that lands on
      // it is told. In the calendar key's own words, whichever end it is; the
      // day is its reason. Measured to fit one row at 360px.
      drafts.push({ kind: "closed", line: ["No drop-off / collection", d] });
    }
  }

  const limitedNight = nights.find((n) => at(n).level === "limited");
  if (limitedNight) {
    const d = shortDay(limitedNight);
    // Freddi's words (2026-10-08); "submit request soon" did not fit one row at
    // 360px — measured — so the reason is the shorter "request soon".
    drafts.push({ kind: "limited", line: [`${d} has limited availability`, "request soon"] });
  }

  if (sundays.length > 0) {
    const d = shortDay(sundays[0]!);
    // "on Sundays" only when it IS a Sunday: Patricia's dated rows make any day
    // afternoon only, and "5–6pm on Sundays" under Fri 23 is false
    // (handover-days AC10). The kind keeps its name — it is the same note.
    const when = weekday(sundays[0]!) === 0 ? "on Sundays" : "that day";
    drafts.push({ kind: "sunday", line: [`${d} is afternoon only`, `${shortWindow(args.pmWindow)} ${when}`] });
  }

  if (xmas.surcharge > 0 && nights.some((n) => isChristmasNight(n, xmas))) {
    drafts.push({ kind: "christmas", line: [`€${xmas.surcharge} extra a night`, "over Christmas"] });
  }

  // A BOOKABLE RANGE SAYS SO FIRST (Freddi, 2026-10-08): the customer sees
  // their dates confirmed — *available*, true whether or not a night is
  // filling up — and whatever else the dates touch stacks beneath as
  // information. A blocked range is never confirmed.
  // The confirmation is not a condition, so it does not take one of the three
  // places.
  const conditions = drafts.slice(0, MAX_NOTES);
  const bookable = !drafts.some((d) => tone(d.kind) === "stop");
  const span = `${shortDay(checkIn)} – ${shortDay(checkOut)}`;
  // Its reason is the next action, in the button's own words: until both
  // handover times are in it names the one still missing; then it says what
  // the primary does.
  const next =
    dropOff && collection ? "submit your dates"
    : !dropOff && !collection ? "pick both times"
    // One time missing: the empty picker shows which. "pick a collection time"
    // did not fit one row at 360px; measured.
    : "pick a time";
  const kept: Draft[] = bookable
    ? [{ kind: "clear", line: [`${span} is available`, next] }, ...conditions]
    : conditions;
  const notes = kept.map((d) => ({ kind: d.kind, tone: tone(d.kind), headline: d.line[0], body: d.line[1] }));
  const blocked = drafts.some((d) => tone(d.kind) === "stop");
  const ready = !blocked && dropOff !== null && collection !== null;
  return { notes, blocked, ready, dropOff, collection };
}

// ----- the reference --------------------------------------------------------

/**
 * Thirty-one characters nobody reads wrong on a bank screen or over the phone: no
 * 0/O, no 1/I/L. Uniqueness is the database's job — `bookings.reference` is
 * UNIQUE — and the write retries on the rare collision (AC26).
 */
export const REFERENCE_ALPHABET = "23456789ABCDEFGHJKMNPQRSTUVWXYZ";

export function mintReference(random: () => number = Math.random): string {
  let out = "BB-";
  for (let i = 0; i < 4; i++) out += REFERENCE_ALPHABET[Math.floor(random() * REFERENCE_ALPHABET.length)]!;
  return out;
}
